import axios, { AxiosRequestConfig } from 'axios'
import hash from 'object-hash'

import { Palette, range6m1yAll } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataPoint,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getDataSources,
  getMultipleColumnChartProps,
  getMultipleLineChartProps,
  getMultipleLineStrCategoriesChartProps,
  lastPointLabelFormatter,
  magnitudePointFormatter,
} from '../utils/compass'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

export namespace Portfolio {
  export namespace Overview {
    type OverviewChartId =
      | 'exposure_and_prices'
      | 'multiple_ticker'
      | 'earnings_revision'
      | 'yoy_financials'
      | 'rsi_14d'
      | 'insider_trading'
      | 'short_per_float'
      | 'put_call_ratio'

    const OVERVIEW_CHART_PATHS: Record<OverviewChartId, string> = {
      exposure_and_prices: '/overview/exposure-and-prices',
      multiple_ticker: '/overview/multiple-ticker',
      earnings_revision: '/overview/earnings-revision',
      yoy_financials: '/overview/yoy-financials',
      rsi_14d: '/overview/rsi-14d',
      insider_trading: '/overview/insider-trading',
      short_per_float: '/overview/short-per-float',
      put_call_ratio: '/overview/put-call-ratio',
    }

    const CONTEXT_ID = 'equity:portfolio:overview'
    const PORTFOLIO_PATH = '/api/portfolio'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      chart: OverviewChartId
      ticker: string
      fundName?: string
      title?: string
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) =>
      axios(
        modifier({
          url: PORTFOLIO_PATH + OVERVIEW_CHART_PATHS[config.chart],
          method: 'GET',
          params: {
            ticker: config.ticker,
            ...(config.fundName ? { fund_name: config.fundName } : {}),
          },
        })
      ).then((res) => res.data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.chart}:${hash(config)}`,
      getChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'exposure_and_prices':
            return getExposureAndPricesChartProps(config, data)
          case 'multiple_ticker':
          case 'rsi_14d':
          case 'short_per_float':
            return getPeChartProps(config, data)
          case 'earnings_revision':
            return getEarningsRevisionChartProps(config, data)
          case 'yoy_financials':
            return getYoyFinancialsChartProps(config, data)
          case 'insider_trading':
            return getInsiderTradingChartProps(config, data)
          case 'put_call_ratio':
            return getPutCallRatioChartProps(config, data)
        }
      },
    }

    const getExposureAndPricesChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          convertToPercentages: [true, false],
          yAxis: [
            { id: 'absolute', title: { text: 'Price' } },
            {
              id: 'relative',
              labelSuffix: '%',
              opposite: true,
              title: { text: 'Exp. delt.' },
            },
          ],
        },
        {
          yAxis: ['relative', 'absolute'],
        }
      )(config, data)

    const getPeChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        msdIndicators: { avg: true, sd1: true, sd2: true },
      })(config, data)

    const getInsiderTradingChartProps = (
      config: ChartConfig,
      data: ChartData
    ) =>
      getMultipleColumnChartProps(
        Context,
        {
          legend: { maxHeight: 80 },
          plotOptions: { column: { pointWidth: 5 } },
          tooltip: {
            shared: true,
            split: false,
            pointFormatter: magnitudePointFormatter(),
            xDateFormat: '%B %Y',
          },
          xAxis: { labels: { format: '{value:%m/%Y}' } },
          yAxis: [
            { id: 'primary', title: { text: 'Volume' } },
            {
              id: 'secondary',
              opposite: true,
              title: { text: 'Preço médio' },
            },
          ],
        },
        {
          color: [
            ...Array(data.data.length - 1).fill(undefined),
            Palette.green,
          ],
          type: [
            'spline',
            ...Array(data.data.length - 2).fill('column'),
            'spline',
          ],
          yAxis: [...Array(data.data.length - 1).fill('primary'), 'secondary'],
        }
      )(config, data)

    const convertDataToCategory = (data: ChartData) => ({
      ...data,
      data: data.data.map(
        (s: CompassAPIMultiSeriesChartData['data'][number]) => ({
          ...s,
          data: s.data.map((point: CompassAPIDataPoint) => ({
            date: point.date.split('-')[0],
            value: point.value,
          })),
        })
      ),
    })

    const getYoyFinancialsChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const categorizedDate = convertDataToCategory(data)
      return getMultipleLineStrCategoriesChartProps(
        Context,
        {
          convertToPercentages: Array(data.data.length).fill(true),
          xAxisCategories: categorizedDate.data[0].data.map(
            (point) => point.date.split('-')[0]
          ),
        },
        {
          type: Array(data.data.length).fill('column'),
        }
      )(config, categorizedDate)
    }

    const getEarningsRevisionChartProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        data: convertCompassToHighchartsDataFormat(s.data),
        dataLabels: [{ enabled: true, formatter: lastPointLabelFormatter() }],
        id: `${config.chart}-${idx}`,
        name: s.name,
        tooltip: { pointFormatter: magnitudePointFormatter() },
        type: 'line',
        yAxis: 'default',
      }))

      const yAxis = [{ id: 'default', opposite: false, title: { text: '' } }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: mergeDeepAll([
          getDataSources(data.data_sources),
          commonChartOptions,
          {
            rangeSelector: range6m1yAll,
            series,
            yAxis,
          },
        ]),
        stockTools: true,
      }
    }

    const getPutCallRatioChartProps = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(Context, {}, { type: ['spline'] })(config, data)
  }
}
